import { apiV2 } from '@newtontechnologies/beey-api-js-client/endpoints';
import { json, blob, hasErrorMessage, } from '@newtontechnologies/beey-api-js-client/receivers';
import { TeamSettingsSchema } from './settings/team-settings';
import { createUserFromApi, } from './model/user';
export const fetchCurrentTeam = async (connection) => {
    const teamData = await connection.fetch()
        .url(apiV2.currentTeam.url())
        .receive(json())
        .fetch();
    teamData.settings = TeamSettingsSchema.sanitize(teamData.settings);
    return teamData;
};
export const fetchCurrentTeamTranscriptionPerPeriod = async (connection, dates, orderBy, ascending, skip = 0, count = 0) => {
    const params = {
        from: dates[0],
        to: dates[1],
        orderBy,
        ascending,
        skip,
        count,
    };
    return connection.fetch()
        .url(apiV2.currentTeam.transcriptionPerPeriod.url(params))
        .receive(json())
        .fetch();
};
export const fetchCurrentTeamMembers = async (connection, skip = 0, count = 0) => connection.fetch()
    .url(apiV2.currentTeam.members.url({ skip, count }))
    .receive(json())
    .fetch();
export const fetchMemberTranscriptionLog = async (connection, id, dates, skip = 0, count = 0) => {
    const params = {
        memberId: id,
        from: dates[0],
        to: dates[1],
        skip,
        count,
    };
    return connection.fetch()
        .url(apiV2.currentTeam.members.id(id).transcriptionLog.url(params))
        .receive(json())
        .fetch();
};
export const exportMemberTranscriptionLog = async (connection, id, dates) => connection.fetch()
    .url(apiV2.currentTeam.members.id(id).transcriptionLog.export.url(dates.length === 0
    ? undefined
    : {
        from: dates[0],
        to: dates[1],
    }))
    .receive(blob())
    .fetch();
export const storeCurrentTeamSettings = async (connection, settings) => connection.fetch()
    .url(apiV2.currentTeam.settings.url())
    .postJson({ settings })
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchCurrentTeamSettings = async (connection) => {
    const data = await connection.fetch()
        .url(apiV2.currentTeam.settings.url())
        .receive(json())
        .fetch();
    return TeamSettingsSchema.sanitize(data.settings);
};
export const fetchCurrentTeamDefaultTags = async (connection) => {
    const settings = await fetchCurrentTeamSettings(connection);
    return [...settings.teamTags];
};
export const fetchCurrentTeamTags = async (connection, skip, count) => connection.fetch()
    .url(apiV2.currentTeam.tags.url({ skip, count }))
    .receive(json())
    .fetch();
export const addCurrentTeamTag = async (connection, tag, abbreviation, color, isPinned) => connection.fetch()
    .url(apiV2.currentTeam.tags.url())
    .postJson({
    tag,
    abbreviation,
    color,
    isPinned,
})
    .receive(json()
    .catchHttpError((response) => (response.status === 422 ? { error: 'duplicate-tag' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const updateCurrentTeamTag = async (connection, tagId, abbreviation, color, isPinned) => connection.fetch()
    .url(apiV2.currentTeam.tags.id(tagId).url())
    .putJson({
    abbreviation,
    color,
    isPinned,
})
    .receive(json()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const deleteCurrentTeamTag = async (connection, tagId) => connection.fetch()
    .url(apiV2.currentTeam.tags.id(tagId).url())
    .delete()
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'not-found' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const teamOwnerAbandonProjectsIn = async (connection, abandonProjects) => {
    const body = {
        Age: abandonProjects.inDays === null ? null : `${String(abandonProjects.inDays)}.00:00:00`,
    };
    return connection.fetch()
        .url(apiV2.currentTeam.abandonProjectsIn.url())
        .postJson(body)
        .receive(json()
        .finally(() => 'unexpected'))
        .fetchStrict();
};
export const fetchExpiringCredit = async (connection) => connection.fetch()
    .url(apiV2.currentTeam.expiringCredit.url())
    .receive(json())
    .fetch();
export const fetchTeamSubscriptions = async (connection) => connection.fetch()
    .url(apiV2.currentTeam.subscriptions.url())
    .receive(json())
    .fetch();
export const fetchExportTemplates = async (connection) => connection.fetch()
    .url(apiV2.currentTeam.exportTemplates.url())
    .receive(json())
    .fetch();
export const fetchTeamStorageStatistics = async (connection) => connection.fetch()
    .url(apiV2.currentTeam.storageStatistics.url())
    .receive(json())
    .fetch();
export const deleteTeamMember = async (connection, userId) => connection.fetch()
    .url(apiV2.currentTeam.members.id(userId).url())
    .delete()
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'not-found' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const addTeamMember = async (connection, email, language, sendEmail = true) => connection.fetch()
    .url(apiV2.currentTeam.members.url())
    .postJson({
    sendEmail,
    member: {
        email,
        language,
    },
})
    .receive(json()
    .map(createUserFromApi)
    .catchHttpError((response, errorPayload) => ((response.status === 422 && hasErrorMessage(errorPayload, 'Email already used: email')) ? { error: 'duplicate-email' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const transferOwnership = async (connection, newOwnerId) => {
    const body = { NewOwnerId: newOwnerId };
    return (connection.fetch()
        .url(apiV2.currentTeam.transferOwnership.url())
        .putJson(body)
        .send());
};
export const exportLexiconRules = async (connection, language) => connection.fetch()
    .url(apiV2.currentTeam.lexicon.export.url({ language }))
    .receive(blob())
    .fetch();
export const fetchSubscriptions = async (connection) => {
    return connection.fetch()
        .url(apiV2.currentTeam.subscriptions.url())
        .receive(json()
        .catchHttpError((response) => {
        switch (response.status) {
            case 403:
                return { error: 'forbidden' };
            case 404:
                return { error: 'not-found' };
            case 422:
                return { error: 'invalid-data' };
            default:
                return 'unknown';
        }
    })
        .finally(() => 'unexpected'))
        .fetchStrict();
};
export const activateSubscription = async (connection, subscriptionId) => connection.fetch()
    .url(apiV2.currentTeam.subscriptions.id(subscriptionId).activate.url())
    .receive(json()
    .catchHttpError((response) => {
    switch (response.status) {
        case 403:
            return { error: 'forbidden' };
        case 404:
            return { error: 'not-found' };
        case 400:
            return { error: 'deactivate' };
        case 422:
            return { error: 'failed' };
        default:
            return 'unknown';
    }
})
    .finally(() => 'unexpected'))
    .fetchStrict();
export const cancelSubscription = async (connection, subscriptionId) => connection.fetch()
    .url(apiV2.currentTeam.subscriptions.id(subscriptionId).cancel.url())
    .post()
    .receive(json()
    .catchHttpError((response) => {
    switch (response.status) {
        case 403:
            return { error: 'forbidden' };
        case 404:
            return { error: 'not-found' };
        case 422:
            return { error: 'failed' };
        default:
            return 'unknown';
    }
})
    .finally(() => 'unexpected'))
    .fetchStrict();
export const changeSubscription = async (connection, subscriptionId, planId) => connection.fetch()
    .url(apiV2.currentTeam.subscriptions.id(subscriptionId).changePlan.url())
    .postJson({
    PlanId: planId,
})
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'not-found' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchAvailablePlans = async (connection, sourceSystem) => connection.fetch()
    .url(apiV2.currentTeam.subscriptions.availablePlans.url({ sourceSystem }))
    .receive(json())
    .fetch();
